/**
 * Character XP & level-up logic (HEX-117 / ADR-0018 §3).
 *
 * XP sources:
 *   - tile-cross: +XP_TILE_CROSS per NEWLY-crossed hex (per-character explored
 *     set — not global discovery; no double-count on re-cross).
 *   - combat-victory: +XP_COMBAT_VICTORY[tier] after winning an encounter.
 *
 * Level curve: cumulative XP to reach level N = 50 * (N-1) * N.
 *   L2 = 100 · L3 = 300 · L4 = 600 · L5 = 1000 · … · L10 = 4500
 *
 * On level-up: full HP heal + 3-pick offer (≤1 specialist die, rest gameplay
 * cards). Run-scoped — level resets on character death.
 */

import { GAMEPLAY_CARDS } from '../combat/cardDrops';
import type { DieType, Rng } from '../combat/dice';
import { SPECIALIST_DICE } from '../combat/diceRoster';
import type { Card, EnemyTier } from '../combat/types';
import type { Character } from '../core/character';

export const XP_TILE_CROSS = 10;

export const XP_COMBAT_VICTORY: Record<EnemyTier, number> = {
  common: 25,
  rare: 60,
  epic: 120,
  legendary: 250,
};

export const XP_MAX_LEVEL = 10;

/** Cumulative XP required to reach level N (1-based). Level 1 requires 0. */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return 50 * (level - 1) * level;
}

/** Derive the current level (1..XP_MAX_LEVEL) from accumulated XP. */
export function levelForXp(xp: number): number {
  let level = 1;
  while (level < XP_MAX_LEVEL && xp >= xpForLevel(level + 1)) {
    level++;
  }
  return level;
}

/**
 * Award `amount` XP to the character; bump `character.level` if one or more
 * thresholds are crossed. Mutates `character.xp` and `character.level` in place.
 * Returns `levelsGained` — the number of levels crossed (0 if none) — so a
 * single big award (e.g. a boss kill spanning two levels) can fire one reward
 * overlay per level gained. The caller applies the full-heal + overlay(s).
 */
export function addXp(
  character: Character,
  amount: number,
): { newXp: number; newLevel: number; levelsGained: number } {
  const prevLevel = character.level ?? 1;
  const newXp = (character.xp ?? 0) + amount;
  const newLevel = levelForXp(newXp);
  character.xp = newXp;
  character.level = newLevel;
  return { newXp, newLevel, levelsGained: Math.max(0, newLevel - prevLevel) };
}

export type LevelUpOffer = { kind: 'card'; card: Card } | { kind: 'die'; die: DieType };

/** Number of slots in a single level-up offer. */
const OFFER_SIZE = 3;

/**
 * Roll 3 distinct level-up reward offers. 50% chance that one slot is a
 * specialist die; the remaining slots are gameplay cards drawn WITHOUT
 * replacement (partial Fisher–Yates), so a single offer never repeats a card.
 * Deterministic under a given seed. Consumes 4 rng() draws (fewer only if a
 * pool is exhausted). If the distinct card pool is smaller than the slots to
 * fill, the offer holds whatever distinct cards exist — no crash, no loop.
 */
export function rollLevelUpOffers(rng: Rng): LevelUpOffer[] {
  const offers: LevelUpOffer[] = [];
  if (rng() < 0.5 && SPECIALIST_DICE.length > 0) {
    const idx = Math.min(SPECIALIST_DICE.length - 1, Math.floor(rng() * SPECIALIST_DICE.length));
    const die = SPECIALIST_DICE[idx];
    if (die !== undefined) offers.push({ kind: 'die', die });
  }
  // Fill the remaining slots with distinct cards via a partial Fisher–Yates
  // shuffle: swap each prefix index with a random later (or equal) index, then
  // take that slot. Drawing without replacement guarantees no duplicates; the
  // loop is bounded by the pool length → graceful fallback when the pool is
  // smaller than the slots to fill.
  const pool = [...GAMEPLAY_CARDS];
  const cardsWanted = OFFER_SIZE - offers.length;
  for (let i = 0; i < cardsWanted && i < pool.length; i++) {
    const j = Math.min(pool.length - 1, i + Math.floor(rng() * (pool.length - i)));
    const picked = pool[j];
    const swapped = pool[i];
    if (picked === undefined || swapped === undefined) continue;
    pool[j] = swapped;
    pool[i] = picked;
    offers.push({ kind: 'card', card: picked });
  }
  return offers;
}
